import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  ChevronsDownUp,
  ChevronsUpDown,
  ListTree,
  Loader2,
  Network,
  Search,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { employeeApi, locationApi } from '../services/api';
import type { EmployeeHierarchyNode, Location, OrganizationHierarchyResponse } from '../types';
import {
  OrgChartOutline,
  OrgChartPersonCard,
  OrgChartTree,
  collectExpandableIds,
  collectMatchingIds,
  countPeople,
  expandIdsForMatches,
  idsExpandedToDepth,
} from '../components/OrgChartNodes';
import { useLocationContext } from '../components/Layout';

type ChartView = 'chart' | 'outline';

function splitRoots(roots: EmployeeHierarchyNode[]) {
  const trees: EmployeeHierarchyNode[] = [];
  const unassigned: EmployeeHierarchyNode[] = [];
  for (const root of roots) {
    if ((root.children ?? []).length > 0) trees.push(root);
    else unassigned.push(root);
  }
  trees.sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
  unassigned.sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
  return { trees, unassigned };
}

const OrganizationChart: React.FC = () => {
  const navigate = useNavigate();
  const { selectedLocationId, setSelectedLocationId, locations } = useLocationContext();
  const [data, setData] = useState<OrganizationHierarchyResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locationDetail, setLocationDetail] = useState<Location | null>(null);
  const [view, setView] = useState<ChartView>('chart');
  const [query, setQuery] = useState('');
  const [zoom, setZoom] = useState(1);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (selectedLocationId == null) {
      setData(null);
      setError(null);
      setLocationDetail(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    setQuery('');

    Promise.all([
      employeeApi.getOrganizationHierarchy(selectedLocationId),
      locationApi.getById(selectedLocationId).catch(() => null),
    ])
      .then(([hierarchyRes, locRes]) => {
        if (!cancelled) {
          setData(hierarchyRes);
          setLocationDetail(locRes);
          const { trees } = splitRoots(hierarchyRes.roots);
          setExpandedIds(idsExpandedToDepth(trees, 1));
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const msg =
            err && typeof err === 'object' && 'response' in err
              ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
              : null;
          setError(msg || 'Could not load organization chart.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedLocationId]);

  const openProfile = (id: number) => {
    navigate(`/employees/${id}`);
  };

  const selectedLocationName =
    selectedLocationId != null ? locations.find((l) => l.id === selectedLocationId)?.name : null;

  const locationManager = locationDetail?.manager ?? locations.find((l) => l.id === selectedLocationId)?.manager;

  const { trees, unassigned } = useMemo(() => {
    if (!data) return { trees: [] as EmployeeHierarchyNode[], unassigned: [] as EmployeeHierarchyNode[] };
    const split = splitRoots(data.roots);
    if (locationManager) {
      split.trees.sort((a, b) => {
        if (a.id === locationManager.id) return -1;
        if (b.id === locationManager.id) return 1;
        return 0;
      });
    }
    return split;
  }, [data, locationManager]);

  const totalPeople = data ? countPeople(data.roots) : 0;
  const allExpandable = useMemo(() => collectExpandableIds(trees), [trees]);

  const matchIds = useMemo(() => collectMatchingIds(data?.roots ?? [], query), [data, query]);
  const searchExpanded = useMemo(() => expandIdsForMatches(trees, matchIds), [trees, matchIds]);

  const visibleExpanded = useMemo(() => {
    if (matchIds.size === 0) return expandedIds;
    const next = new Set(expandedIds);
    searchExpanded.forEach((id) => next.add(id));
    return next;
  }, [expandedIds, matchIds, searchExpanded]);

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const searching = query.trim().length > 0;

  return (
    <div className="w-full max-w-[96%] mx-auto p-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <Link
          to="/employees"
          className="inline-flex items-center gap-2 text-neutral-600 hover:text-neutral-900 text-sm font-medium w-fit"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Employees
        </Link>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div className="flex items-start gap-3 min-w-0">
          <Building2 className="h-8 w-8 text-violet-600 shrink-0 mt-1" />
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-neutral-800">Organization chart</h1>
            <p className="text-sm text-neutral-600 mt-1">
              Reporting lines at the selected location. Expand a manager to see who reports to them.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-1 shrink-0 w-full sm:w-auto sm:min-w-[14rem]">
          <label htmlFor="org-chart-location" className="text-xs font-medium text-neutral-500">
            Location
          </label>
          <select
            id="org-chart-location"
            value={selectedLocationId ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setSelectedLocationId(v ? Number(v) : null);
            }}
            className="w-full sm:w-56 px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
          >
            <option value="">All locations</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden">
        {selectedLocationId != null && data && !loading && !error && (
          <div className="flex flex-col gap-3 border-b border-neutral-200 bg-neutral-50 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-lg border border-neutral-200 bg-white p-0.5">
                <button
                  type="button"
                  onClick={() => setView('chart')}
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${
                    view === 'chart' ? 'bg-violet-600 text-white' : 'text-neutral-600 hover:bg-neutral-100'
                  }`}
                >
                  <Network className="h-4 w-4" />
                  Chart
                </button>
                <button
                  type="button"
                  onClick={() => setView('outline')}
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${
                    view === 'outline' ? 'bg-violet-600 text-white' : 'text-neutral-600 hover:bg-neutral-100'
                  }`}
                >
                  <ListTree className="h-4 w-4" />
                  Outline
                </button>
              </div>
              <button
                type="button"
                onClick={() => setExpandedIds(new Set(allExpandable))}
                className="inline-flex items-center gap-1 rounded-md border border-neutral-200 bg-white px-2.5 py-1.5 text-sm text-neutral-700 hover:bg-neutral-100"
              >
                <ChevronsUpDown className="h-4 w-4" />
                Expand all
              </button>
              <button
                type="button"
                onClick={() => setExpandedIds(new Set())}
                className="inline-flex items-center gap-1 rounded-md border border-neutral-200 bg-white px-2.5 py-1.5 text-sm text-neutral-700 hover:bg-neutral-100"
              >
                <ChevronsDownUp className="h-4 w-4" />
                Collapse all
              </button>
              {view === 'chart' && (
                <div className="inline-flex items-center rounded-md border border-neutral-200 bg-white">
                  <button
                    type="button"
                    onClick={() => setZoom((z) => Math.max(0.6, Math.round((z - 0.1) * 10) / 10))}
                    className="p-1.5 text-neutral-600 hover:bg-neutral-100"
                    aria-label="Zoom out"
                  >
                    <ZoomOut className="h-4 w-4" />
                  </button>
                  <span className="min-w-[3rem] text-center text-xs text-neutral-500">{Math.round(zoom * 100)}%</span>
                  <button
                    type="button"
                    onClick={() => setZoom((z) => Math.min(1.4, Math.round((z + 0.1) * 10) / 10))}
                    className="p-1.5 text-neutral-600 hover:bg-neutral-100"
                    aria-label="Zoom in"
                  >
                    <ZoomIn className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            <div className="relative w-full lg:w-72">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Find a person or role"
                className="w-full rounded-md border border-neutral-300 bg-white py-1.5 pl-8 pr-3 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
          </div>
        )}

        <div className="p-6">
          {selectedLocationId == null && (
            <div className="text-center py-16 px-4">
              <p className="text-neutral-700 font-medium">Select a location</p>
              <p className="text-sm text-neutral-500 mt-2 max-w-md mx-auto">
                Choose a location above to view the reporting hierarchy for employees at that site. &quot;All
                locations&quot; is not available here—pick one location to load the chart.
              </p>
            </div>
          )}

          {selectedLocationId != null && loading && (
            <div className="flex flex-col items-center justify-center py-24 gap-3 text-neutral-600">
              <Loader2 className="h-10 w-10 animate-spin text-violet-500" />
              <span className="text-sm">Loading organization chart…</span>
            </div>
          )}

          {selectedLocationId != null && error && !loading && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">{error}</div>
          )}

          {selectedLocationId != null && data && !loading && !error && data.roots.length === 0 && (
            <p className="text-center text-neutral-600 py-12">No employees at this location for the chart.</p>
          )}

          {selectedLocationId != null && data && !loading && !error && data.roots.length > 0 && (
            <>
              {searching && (
                <p className="mb-4 text-sm text-neutral-600">
                  {matchIds.size === 0
                    ? 'No people match that search.'
                    : `${matchIds.size} ${matchIds.size === 1 ? 'person matches' : 'people match'} — matching cards are highlighted.`}
                </p>
              )}

              {view === 'chart' ? (
                <div className="overflow-auto rounded-lg bg-[radial-gradient(#e5e5e5_1px,transparent_1px)] [background-size:16px_16px] p-6 min-h-[28rem]">
                  <div style={{ zoom }} className="origin-top">
                    {trees.length > 0 ? (
                      <OrgChartTree
                        nodes={trees}
                        onOpenProfile={openProfile}
                        expandedIds={visibleExpanded}
                        onToggleExpand={toggleExpand}
                        highlightedIds={searching ? matchIds : undefined}
                        dimUnmatched={searching}
                        header={
                          <div className="org-tree-card w-[15rem] rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-center shadow-sm">
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-violet-700">
                              Location
                            </div>
                            <div className="mt-0.5 text-sm font-semibold text-neutral-900">
                              {selectedLocationName ?? 'Selected location'}
                            </div>
                            {locationManager ? (
                              <button
                                type="button"
                                onClick={() => openProfile(locationManager.id)}
                                className="mt-1 text-xs font-medium text-violet-700 hover:underline"
                              >
                                Manager: {locationManager.firstName} {locationManager.lastName}
                              </button>
                            ) : (
                              <div className="mt-1 text-xs text-neutral-500">No location manager</div>
                            )}
                            <div className="mt-1 text-[11px] text-neutral-500">
                              {totalPeople} {totalPeople === 1 ? 'person' : 'people'}
                            </div>
                          </div>
                        }
                      />
                    ) : (
                      <div className="mx-auto max-w-lg text-center py-6">
                        <p className="font-medium text-neutral-800">No reporting lines at this location</p>
                        <p className="mt-1 text-sm text-neutral-500">
                          Everyone listed below has no line manager in this location, so there is no hierarchy to draw.
                          Assign line managers on employee profiles to build the tree.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="max-w-3xl">
                  {trees.length > 0 ? (
                    <OrgChartOutline
                      nodes={trees}
                      onOpenProfile={openProfile}
                      expandedIds={visibleExpanded}
                      onToggleExpand={toggleExpand}
                      highlightedIds={searching ? matchIds : undefined}
                      dimUnmatched={searching}
                    />
                  ) : (
                    <p className="text-sm text-neutral-500">
                      No reporting lines to show. People without a line manager are listed below.
                    </p>
                  )}
                </div>
              )}

              {unassigned.length > 0 && (!searching || unassigned.some((person) => matchIds.has(person.id))) && (
                <section className="mt-8 border-t border-neutral-200 pt-6">
                  <h2 className="text-sm font-semibold text-neutral-800">
                    No line manager at this location
                    <span className="ml-2 font-normal text-neutral-500">({unassigned.length})</span>
                  </h2>
                  <p className="mt-1 mb-4 text-xs text-neutral-500">
                    These people are not connected to a reporting tree here. They stay out of the chart so the hierarchy
                    stays readable.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {unassigned
                      .filter((person) => !searching || matchIds.has(person.id))
                      .map((person) => (
                        <OrgChartPersonCard
                          key={person.id}
                          person={person}
                          onOpenProfile={openProfile}
                          highlighted={searching && matchIds.has(person.id)}
                        />
                      ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganizationChart;
